/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { Layout } from './components/common/Layout'
import { Header } from './components/common/Header'
import { Spinner } from './components/common/Spinner'
import { ProtectedRoute } from './components/auth/ProtectedRoute'

// Lazy loading des écrans pour code splitting
// Les écrans d'auth sont chargés immédiatement (critiques)
import { LoginScreen } from './screens/LoginScreen'
import { RegisterScreen } from './screens/RegisterScreen'
import { ForgotPasswordScreen } from './screens/ForgotPasswordScreen'

// Lazy loading pour tous les autres écrans
const HomeScreen = lazy(() =>
  import('./screens/HomeScreen').then((m) => ({ default: m.HomeScreen }))
)
const ReadingScreen = lazy(() =>
  import('./screens/ReadingScreen').then((m) => ({ default: m.ReadingScreen }))
)
const TextDetailScreen = lazy(() =>
  import('./screens/TextDetailScreen').then((m) => ({ default: m.TextDetailScreen }))
)

const AboutScreen = lazy(() =>
  import('./screens/AboutScreen').then((m) => ({ default: m.AboutScreen }))
)

const ChangePasswordScreen = lazy(() =>
  import('./screens/ChangePasswordScreen').then((m) => ({ default: m.ChangePasswordScreen }))
)
const LicensesScreen = lazy(() =>
  import('./screens/LicensesScreen').then((m) => ({ default: m.LicensesScreen }))
)
const VerifyEmailSentScreen = lazy(() =>
  import('./screens/VerifyEmailSentScreen').then((m) => ({ default: m.VerifyEmailSentScreen }))
)

const GroupDetailScreen = lazy(() =>
  import('./screens/GroupDetailScreen').then((m) => ({ default: m.GroupDetailScreen }))
)

const CreateGroupScreen = lazy(() =>
  import('./screens/CreateGroupScreen').then((m) => ({ default: m.CreateGroupScreen }))
)
const HelpScreen = lazy(() =>
  import('./screens/HelpScreen').then((m) => ({ default: m.HelpScreen }))
)
const JoinGroupScreen = lazy(() =>
  import('./screens/JoinGroupScreen').then((m) => ({ default: m.JoinGroupScreen }))
)
const CreateTextScreen = lazy(() =>
  import('./screens/CreateTextScreen').then((m) => ({ default: m.CreateTextScreen }))
)

import { useParams } from 'react-router-dom'
import { APP_NAME } from './config/env'
import { useAppMenu } from './hooks/useAppMenu'

/**
 * Composant wrapper pour la page d'accueil avec menu
 */
function LibraryPage() {
  const { menuItems } = useAppMenu()

  return (
    <Layout header={<Header title={APP_NAME} showLogo menuItems={menuItems} hideTheme />}>
      <HomeScreen />
    </Layout>
  )
}

/**
 * Wrapper pour ReadingScreen
 */
function ReadingScreenWrapper() {
  const params = useParams<{ playId?: string; textId?: string }>()
  const id = params.textId || params.playId // Support des deux formats

  // Ne pas utiliser readingMode dans la key pour éviter le démontage lors du passage en mode édition
  // Le composant ReadingScreen gère lui-même les changements de mode
  return <ReadingScreen key={id} />
}

/**
 * Configuration des routes de l'application
 */
const router = createBrowserRouter(
  [
    // Routes publiques (auth)
    {
      path: '/login',
      element: <LoginScreen />,
    },
    {
      path: '/register',
      element: <RegisterScreen />,
    },
    {
      path: '/forgot-password',
      element: <ForgotPasswordScreen />,
    },

    {
      path: '/change-password',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <ChangePasswordScreen />
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: '/verify-email-sent',
      element: (
        <Suspense
          fallback={
            <div className="flex items-center justify-center min-h-screen">
              <Spinner size="lg" />
            </div>
          }
        >
          <VerifyEmailSentScreen />
        </Suspense>
      ),
    },

    // Routes protégées (nécessitent authentification)
    {
      path: '/',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <LibraryPage />
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: '/texts/new',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <CreateTextScreen />
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: '/texts/:textId/detail',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <TextDetailScreen />
          </Suspense>
        </ProtectedRoute>
      ),
    },

    {
      path: '/text/:textId/read',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <ReadingScreenWrapper />
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: '/play/:playId',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <ReadingScreenWrapper />
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: '/reader/:playId',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <ReadingScreenWrapper />
          </Suspense>
        </ProtectedRoute>
      ),
    },
    // Routes groupes (Phase 6.1)

    {
      path: '/groups/create',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <CreateGroupScreen />
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: '/groups/join',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <JoinGroupScreen />
          </Suspense>
        </ProtectedRoute>
      ),
    },

    {
      path: '/groups/:groupId',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <GroupDetailScreen />
          </Suspense>
        </ProtectedRoute>
      ),
    },

    // Routes publiques (info)
    {
      path: '/help',
      element: (
        <ProtectedRoute>
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <Spinner size="lg" />
              </div>
            }
          >
            <HelpScreen />
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: '/about',
      element: (
        <Suspense
          fallback={
            <div className="flex items-center justify-center min-h-screen">
              <Spinner size="lg" />
            </div>
          }
        >
          <AboutScreen />
        </Suspense>
      ),
    },
    {
      path: '/licenses',
      element: (
        <Suspense
          fallback={
            <div className="flex items-center justify-center min-h-screen">
              <Spinner size="lg" />
            </div>
          }
        >
          <LicensesScreen />
        </Suspense>
      ),
    },
  ],
  {
    basename: import.meta.env.BASE_URL.replace(/\/$/, ''), // Enlever le slash final
  }
)

/**
 * Composant Router principal
 */
export function Router() {
  return <RouterProvider router={router} />
}
